import React, { useLayoutEffect, useMemo } from 'react';

import { Box, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { BODY_DARK_THEME_CLASS } from '../../../../context/ThemeContext';
import { UiAlpha } from '../../../../styles/ui-alpha';
import { createAppThemeFromLocalStorage } from '../../../../utils/theme/create-app-theme-from-local-storage';
import AppButton from '../../../designe/buttons/app-button/AppButton';
import { useStyles } from './CommonErrorPage.styles';

export type ICommonErrorTone = 'notFound' | 'server' | 'forbidden';

interface ICommonErrorPageProps {
  /** URL for legacy raster/SVG-as-img usage */
  errorImage?: string | null;
  /** SVG as React component — supports `currentColor` for theme-aware strokes */
  errorIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Color for `errorIcon` strokes (ignored for `errorImage` unless `errorTone` is set) */
  iconColor?: string;
  title: string;
  description: string;
  buttonText: string;
  onClick?: () => void; // Optional function to redirect to home page on button click. Default is to redirect to home page.
  children?: React.ReactNode;
  statusCode?: string;
  statusCodeColor?: string;
  iconCircleColor?: string;
  iconSize?: number;
  iconCircleSize?: number;
  /**
   * When set, accent colors are derived inside the local ThemeProvider (works under ErrorBoundary).
   */
  errorTone?: ICommonErrorTone;
}

function CommonErrorPageInner({
  errorImage,
  errorIcon,
  iconColor: iconColorProp,
  title,
  description,
  buttonText,
  onClick,
  children,
  statusCode,
  statusCodeColor: statusCodeColorProp,
  iconCircleColor: iconCircleColorProp,
  iconSize = 52,
  iconCircleSize = 112,
  errorTone,
}: ICommonErrorPageProps) {
  const theme = useTheme();
  const { classes } = useStyles(theme);

  const derivedColors = useMemo(() => {
    if (!errorTone) {
      return null;
    }
    if (errorTone === 'notFound') {
      const c = theme.palette.text.secondary;
      return {
        statusCodeColor: c,
        iconColor: c,
        iconCircleColor: alpha(c, UiAlpha.Tint),
      };
    }
    if (errorTone === 'server') {
      const c = theme.palette.error.main;
      return {
        statusCodeColor: c,
        iconColor: c,
        iconCircleColor: alpha(c, UiAlpha.Tint),
      };
    }
    const c = theme.palette.warning.main;
    return {
      statusCodeColor: c,
      iconColor: c,
      iconCircleColor: alpha(c, UiAlpha.Tint),
    };
  }, [errorTone, theme]);

  const statusCodeColor = derivedColors?.statusCodeColor ?? statusCodeColorProp;
  const iconCircleColor = derivedColors?.iconCircleColor ?? iconCircleColorProp;
  const iconColor = derivedColors?.iconColor ?? iconColorProp;

  const goToHomePage = () => {
    window.location.href = '/';
  };

  const showIcon = Boolean(errorIcon || errorImage);
  const ErrorIconComponent = errorIcon;

  return (
    <div className={classes.container}>
      <CssBaseline />
      <div className={classes.content}>
        {showIcon && (
          <Box
            className={classes.iconCircle}
            sx={{
              backgroundColor: iconCircleColor ?? undefined,
              width: iconCircleSize,
              height: iconCircleSize,
              ...(ErrorIconComponent && {
                color: iconColor ?? statusCodeColor,
              }),
            }}>
            {ErrorIconComponent ? (
              <ErrorIconComponent
                className={classes.errorImage}
                style={{ width: iconSize, height: iconSize }}
                aria-hidden
              />
            ) : (
              errorImage && (
                <img
                  src={errorImage}
                  alt=""
                  className={classes.errorImage}
                  style={{ width: iconSize, height: iconSize }}
                />
              )
            )}
          </Box>
        )}
        <div className={classes.mainContent}>
          {statusCode && (
            <Typography
              variant="h1"
              className={classes.statusCode}
              style={{ color: statusCodeColor ?? undefined }}>
              {statusCode}
            </Typography>
          )}
          <Typography variant="h3" className={classes.title}>
            {title}
          </Typography>
          <Typography variant="text-lg" className={clsx(classes.description)}>
            {description}
          </Typography>
          {children}
          {buttonText && (
            <AppButton
              className={classes.button}
              variant="contained"
              onClick={onClick ? onClick : goToHomePage}>
              {buttonText}
            </AppButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommonErrorPage(props: ICommonErrorPageProps) {
  const theme = useMemo(() => createAppThemeFromLocalStorage(), []);

  useLayoutEffect(() => {
    let addedByThisComponent = false;

    if (theme.palette.mode === 'dark') {
      if (!document.body.classList.contains(BODY_DARK_THEME_CLASS)) {
        document.body.classList.add(BODY_DARK_THEME_CLASS);
        addedByThisComponent = true;
      }
    }

    return () => {
      if (addedByThisComponent) {
        document.body.classList.remove(BODY_DARK_THEME_CLASS);
      }
    };
  }, [theme]);

  return (
    <ThemeProvider theme={theme}>
      <CommonErrorPageInner {...props} />
    </ThemeProvider>
  );
}
